import React from "react";
import { withTooltip } from "../../hocs";
import Loader from "../Loader";
import * as S from "./styles";
import { BaseButtonProps } from "./types";

type BaseButtonDisplayableProps<C extends React.ElementType = "button"> = BaseButtonProps<C> & {
    /**
     * Defines the displayable text for label.
     */
    title?: string;
    /**
     * Defines an displayable icon on the left side.
     */
    icon?: React.ReactNode;
};

export const BaseButton = withTooltip(
    React.forwardRef(
        <C extends React.ElementType = "button">(
            {
                as,
                title,
                onClick,
                size = "sm",
                variant,
                destructive = false,
                icon,
                endIcon,
                loading = false,
                ...props
            }: BaseButtonDisplayableProps<C>,
            ref?: React.ComponentPropsWithRef<C>["ref"]
        ) => {
            const handleClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
                e => {
                    onClick?.(e);
                },
                [onClick]
            );

            const iconOnly: boolean = !!icon && !title;

            return (
                <S.Button
                    /**
                     * Styled component cannot find proper typing for the component with generic `as`.
                     * It refers to `StyledComponentBase`, instead of `StyledComponentPropsWithAs`,
                     * which only takes in `undefined` value.
                     */
                    as={as as unknown as React.ElementType | undefined}
                    ref={ref}
                    onClick={handleClick}
                    $size={size}
                    $variant={variant}
                    $destructive={destructive}
                    $iconOnly={iconOnly}
                    {...props}
                >
                    {icon && <S.IconWrapper>{icon}</S.IconWrapper>}
                    {!!title && <span>{title}</span>}
                    {endIcon && <S.IconWrapper>{endIcon}</S.IconWrapper>}
                    {loading && (
                        <S.LoaderWrapper $destructive={destructive}>
                            <Loader size={1.5} />
                        </S.LoaderWrapper>
                    )}
                </S.Button>
            );
        }
    )
);
